import React, {useCallback, useEffect, useState} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from "@mui/material";
import FieldHelpSuffix from "../../common/FieldHelpSuffix";
import {ActionButton, CancelButton} from "../../common/ActionButtons";
import {useTranslation} from "react-i18next";
import {useSnackbar} from "../../../contexts/SnackbarContext";
import {
    JobFormState,
    JobParamsRequest,
    JobResponse,
    JobSharePointRequest,
    JobType,
    StorageLocation
} from "../../../models/jobs.models";
import type {DataSource} from "../../../models/models";
import {collectionService} from "../../../services/collection.service";
import {parseError} from "../../../errors/http.service";
import {jobsService} from "../../../services/jobs.service";
import InputIcon from "@mui/icons-material/Input";
import OutputIcon from "@mui/icons-material/Output";
import CreateDataSourceDialog from "../../data-sources/CreateDataSourceDialog";
import CronConfigDialog from "../CronConfigDialog";
import {useFeatureConfig} from "../../../contexts/FeatureConfigContext";
import {FEATURE_KEYS} from "../../../models/admin-config.models";
import {useNavigate} from "react-router-dom";

const formatToFormData = (job: JobResponse): JobFormState => ({
    cronExpression: job.cronExpression,
    targetCollectionName: job.targetCollectionName ?? '',
    targetCollectionId: job.targetCollectionId > 0 ? job.targetCollectionId : '',
    sourceCollectionId: job.sourceCollectionId > 0 ? job.sourceCollectionId : '',
    inputPath: job.type === JobType.SHAREPOINT ? (job.sharePointFolderPath ?? '') : (job.inputPath ?? ''),
    outputPath: job.outputPath ?? '',
    prompt: job.prompt ?? '',
    type: job.type,
});

const detectSourceLocation = (job: JobResponse): StorageLocation => {
    if (job.type === JobType.SHAREPOINT) return StorageLocation.SERVER;
    return (job.sourceCollectionId > 0) ? StorageLocation.COLLECTION : StorageLocation.SERVER;
};

const detectTargetLocation = (job: JobResponse): StorageLocation => {
    if (job.type === JobType.SHAREPOINT) return StorageLocation.COLLECTION;
    return (job.targetCollectionId > 0) ? StorageLocation.COLLECTION : StorageLocation.SERVER;
};

const validateForm = (
    formData: JobFormState,
    sourceLocation: StorageLocation,
    targetLocation: StorageLocation,
): boolean => {
    const { inputPath, sourceCollectionId, outputPath, targetCollectionId, cronExpression, prompt } = formData;

    const isInputValid = sourceLocation === StorageLocation.SERVER
        ? Boolean(inputPath?.trim())
        : Boolean(sourceCollectionId);

    const isOutputValid = targetLocation === StorageLocation.SERVER
        ? Boolean(outputPath?.trim())
        : Boolean(targetCollectionId);

    return isInputValid && isOutputValid && Boolean(cronExpression.trim()) && Boolean(prompt?.trim());
};

interface EditUpdateSchedulerDialogPropsType {
    open: boolean;
    currentFormData: JobResponse;
    onClose: () => void;
    onJobEdited: () => void;
}

type DialogState =
    | { type: 'create' }
    | { type: 'cron'; job: JobFormState }
    | null;

const EditUpdateSchedulerDialog = ({open, currentFormData, onClose, onJobEdited}: EditUpdateSchedulerDialogPropsType) => {
    const {t} = useTranslation();
    const {showSnackbar} = useSnackbar();
    const {isFeatureEnabled} = useFeatureConfig();
    const navigate = useNavigate();

    const isSharePointEnabled = isFeatureEnabled(FEATURE_KEYS.SHAREPOINT_KEY);

    const isLocal = currentFormData.type === JobType.LOCAL;
    const isSharePoint = currentFormData.type === JobType.SHAREPOINT;

    const [formData, setFormData] = useState<JobFormState>(formatToFormData(currentFormData));

    const [sourceLocation, setSourceLocation] = useState<StorageLocation>(detectSourceLocation(currentFormData));
    const [targetLocation, setTargetLocation] = useState<StorageLocation>(detectTargetLocation(currentFormData));

    const [collections, setCollections] = useState<DataSource[]>([]);

    const [loading, setLoading] = useState(false);
    const [collectionsLoading, setCollectionsLoading] = useState(false);

    const [openDialog, setOpenDialog] = useState<DialogState>(null);

    const isFormValid = validateForm(formData, sourceLocation, targetLocation);

    const handleFetchCollections = useCallback(async () => {
        collectionService.invalidateCache();
        setCollectionsLoading(true);

        try {
            const availableCollections = await collectionService.fetchCollections();
            setCollections(availableCollections?.content ?? []);
        } catch (err) {
            console.error("Error fetching collections:", err);
            showSnackbar(parseError(err), "error");
        } finally {
            setCollectionsLoading(false);
        }
    }, [showSnackbar]);

    useEffect(() => {
        void handleFetchCollections();
    }, []);

    useEffect(() => {
        setFormData(formatToFormData(currentFormData));

        setSourceLocation(detectSourceLocation(currentFormData));
        setTargetLocation(detectTargetLocation(currentFormData));
    }, [currentFormData]);

    const handleFormChange = (field: keyof JobFormState, value: string | number) => {
        setFormData(prev => ({...prev, [field]: value}));
    };

    const handleToggleLocation = (data: 'source' | 'target', location: StorageLocation) => {
        if (data === 'source') setSourceLocation(location);
        else setTargetLocation(location);
    };

    const handleSubmit = async () => {
        if (isLocal && (!isFormValid || !currentFormData.id)) {
            showSnackbar(t('editUpdateScheduler.allFieldsRequired'), "error");
            return;
        }
        setLoading(true);

        const { inputPath, outputPath, sourceCollectionId, targetCollectionId, cronExpression, prompt } = formData;

        const params: JobParamsRequest = {
            cronExpression: cronExpression || '',
            prompt: prompt || undefined,
            inputPath: sourceLocation === StorageLocation.SERVER ? inputPath : undefined,
            outputPath: targetLocation === StorageLocation.SERVER ? outputPath : undefined,
            sourceCollectionId: sourceLocation === StorageLocation.COLLECTION ? (sourceCollectionId || undefined) : undefined,
            targetCollectionId: targetLocation === StorageLocation.COLLECTION ? (targetCollectionId || undefined) : undefined,
        };

        const sharePointParams: JobSharePointRequest = {
            cronExpression: cronExpression || '',
            targetCollectionId: targetCollectionId || undefined,
            sharePointFolderPath: inputPath || undefined,
        };

        try {
            await jobsService.updateJob(currentFormData.id, (isSharePoint ? sharePointParams : params));

            showSnackbar(t('editUpdateScheduler.updateSavedSuccess'), 'success');
            onJobEdited();
            onClose();
        } catch (err) {
            showSnackbar(t('editUpdateScheduler.updateSavedError'), "error");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseDialog = () => {
        setFormData(formatToFormData(currentFormData));
        setSourceLocation(detectSourceLocation(currentFormData));
        setTargetLocation(detectTargetLocation(currentFormData));
        setLoading(false);
        onClose();
    }

    const handleDataSourceCreated = async (newId: number) => {
        await handleFetchCollections();
        handleFormChange('targetCollectionId', newId);
    };

    const handleSaveCron = (newCron: string) => {
        handleFormChange('cronExpression', newCron);
    };

    if (!isSharePointEnabled && isSharePoint) {
        navigate("/");
        showSnackbar(t('editUpdateScheduler.sharepointNotEnabled'), "error");
        return null;
    }

    return (
        <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
            <DialogTitle sx={{padding: '24px 32px 16px'}}>
                {t('editUpdateScheduler.title')}
            </DialogTitle>

            <DialogContent sx={{padding: '16px 32px'}}>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                    {t('editUpdateScheduler.subtitle', {type: currentFormData.type.toUpperCase()})}
                </Typography>

                <Grid container spacing={2} alignItems="flex-start" sx={{mt: 1}}>
                    <Grid item xs={12} md={6}>
                        <Stack spacing={3} direction='column'>
                            <Typography variant="subtitle1">
                                {t('editUpdateScheduler.sourceData')}
                            </Typography>

                            <ToggleButtonGroup value={sourceLocation} exclusive fullWidth
                                               onChange={(e, value) => value && handleToggleLocation('source', value)}>
                                <ToggleButton value={StorageLocation.SERVER}>
                                    {t('editUpdateScheduler.server')}
                                </ToggleButton>
                                <ToggleButton value={StorageLocation.COLLECTION} disabled={isSharePoint}>
                                    {t('editUpdateScheduler.collection')}
                                </ToggleButton>
                            </ToggleButtonGroup>

                            {sourceLocation === StorageLocation.SERVER ?
                                (<TextField
                                    label={t('editUpdateScheduler.inputPath')}
                                    fullWidth
                                    onChange={(e) => handleFormChange('inputPath', e.target.value)}
                                    value={formData['inputPath'] || ''}
                                    required={isLocal && isSharePoint}
                                    disabled={!isLocal && !isSharePoint}
                                    InputProps={{
                                        endAdornment: (
                                            <Tooltip title={t('editUpdateScheduler.help.inputPath')}>
                                                <InputIcon aria-hidden="true" sx={{width: '20px', height: '20px', color: 'text.secondary' }} />
                                            </Tooltip>
                                        ),
                                    }}
                                />)
                                : (<FormControl fullWidth>
                                    <InputLabel required id="source-collection-select-label">{t('editUpdateScheduler.source')}</InputLabel>

                                    <Select
                                        labelId="source-collection-select-label"
                                        value={collectionsLoading ? '' : formData.sourceCollectionId}
                                        label={t('editUpdateScheduler.source')}
                                        required={isLocal}
                                        disabled={!isLocal || isSharePoint || collectionsLoading}
                                        onChange={(e) => handleFormChange('sourceCollectionId', e.target.value as DataSource['id'])}
                                        sx={{
                                            borderRadius: '16px',
                                            '& .MuiSelect-icon': {
                                                right: 40,
                                            },
                                        }}
                                        input={
                                            <OutlinedInput
                                                label={t('editUpdateScheduler.source')}
                                                endAdornment={
                                                    <FieldHelpSuffix
                                                        asAdornment
                                                        title={t('editUpdateScheduler.help.source')}
                                                        ariaLabel={t('editUpdateScheduler.help.source') as string}
                                                    />
                                                }
                                                sx={{ pr: '72px' }}
                                            />
                                        }
                                    >
                                        {collections.length > 0 && (
                                            collections.map(source => (
                                                <MenuItem key={source.id} value={source.id}>{source.name}</MenuItem>
                                            ))
                                        )}
                                    </Select>
                                </FormControl>)
                            }
                        </Stack>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Stack spacing={3} direction='column'>
                            <Typography variant="subtitle1">
                                {t('editUpdateScheduler.targetData')}
                            </Typography>

                            <ToggleButtonGroup value={targetLocation} exclusive fullWidth
                                               onChange={(e, value) => value && handleToggleLocation('target', value)}>
                                <ToggleButton value={StorageLocation.SERVER} disabled={isSharePoint}>
                                    {t('editUpdateScheduler.server')}
                                </ToggleButton>
                                <ToggleButton value={StorageLocation.COLLECTION}>
                                    {t('editUpdateScheduler.collection')}
                                </ToggleButton>
                            </ToggleButtonGroup>

                            {targetLocation === StorageLocation.SERVER ?
                                (<TextField
                                    label={t('editUpdateScheduler.outputPath')}
                                    fullWidth
                                    value={formData.outputPath}
                                    onChange={(e) => handleFormChange('outputPath', e.target.value)}
                                    required={isLocal}
                                    disabled={!isLocal || isSharePoint}
                                    InputProps={{
                                        endAdornment: (
                                            <Tooltip title={t('editUpdateScheduler.help.outputPath')}>
                                                <OutputIcon aria-hidden="true" sx={{width: '20px', height: '20px', color: 'text.secondary' }} />
                                            </Tooltip>
                                        ),
                                    }}
                                />)
                                : (<FormControl fullWidth>
                                        <InputLabel required id="target-collection-select-label">{t('editUpdateScheduler.target')}</InputLabel>

                                        <Select
                                            labelId="target-collection-select-label"
                                            value={collectionsLoading ? '' : formData.targetCollectionId}
                                            label={t('editUpdateScheduler.target')}
                                            required={isLocal || isSharePoint}
                                            disabled={(!isLocal && !isSharePoint) || collectionsLoading}
                                            onChange={(e) => {
                                                if (e.target.value === '__add_new__') return;
                                                handleFormChange('targetCollectionId', e.target.value as DataSource['id']);
                                            }}
                                            sx={{
                                                borderRadius: '16px',
                                                '& .MuiSelect-icon': {
                                                    right: 40,
                                                },
                                            }}
                                            input={
                                                <OutlinedInput
                                                    label={t('editUpdateScheduler.target')}
                                                    endAdornment={
                                                        <FieldHelpSuffix
                                                            asAdornment
                                                            title={t('editUpdateScheduler.help.target')}
                                                            ariaLabel={t('editUpdateScheduler.help.target') as string}
                                                        />
                                                    }
                                                    sx={{ pr: '72px' }}
                                                />
                                            }
                                        >
                                            <MenuItem value="__add_new__" onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setOpenDialog({type: 'create'});
                                            }}>
                                                {t('editUpdateScheduler.addNewDataSource')}
                                            </MenuItem>

                                            {collections.length > 0 && (
                                                collections.map(target => (
                                                    <MenuItem key={target.id} value={target.id}>{target.name}</MenuItem>
                                                ))
                                            )}
                                        </Select>
                                    </FormControl>
                                )
                            }
                        </Stack>
                    </Grid>
                </Grid>

                <Grid container spacing={2} alignItems="center" sx={{mt: 3}}>
                    <Grid item xs>
                        <TextField
                            label={t('editUpdateScheduler.cronExpression')}
                            fullWidth
                            value={formData.cronExpression}
                            onChange={(e) => handleSaveCron(e.target.value)}
                            required
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <FieldHelpSuffix
                                        asAdornment
                                        title={t('editUpdateScheduler.help.cronExpression')}
                                        ariaLabel={t('editUpdateScheduler.help.cronExpression') as string}
                                    />
                                ),
                            }}
                        />
                    </Grid>

                    <Grid item>
                        <Button
                            variant="outlined"
                            onClick={() => setOpenDialog({type: 'cron', job: formData})}
                            sx={{height: '56px', width: '100px'}}
                        >
                            {t('editUpdateScheduler.configure')}
                        </Button>
                    </Grid>
                </Grid>

                <TextField
                    key='prompt'
                    label={t('editUpdateScheduler.prompt')}
                    fullWidth
                    multiline
                    rows={5}
                    value={formData['prompt']}
                    onChange={(e) => handleFormChange('prompt', e.target.value)}
                    required={isLocal}
                    disabled={!isLocal || isSharePoint}
                    sx={{mt: 4}}
                    InputProps={{
                        endAdornment: (
                            <FieldHelpSuffix asAdornment
                                             title={t('editUpdateScheduler.help.prompt')}
                                             ariaLabel={t('editUpdateScheduler.help.prompt') as string}
                            />
                        ),
                    }}
                />
            </DialogContent>

            <DialogActions sx={{padding: '16px 32px 24px', justifyContent: 'flex-end', gap: '8px'}}>
                <CancelButton onClick={handleCloseDialog}>
                    {t('common.cancel')}
                </CancelButton>

                <Box sx={{position: 'relative'}}>
                    <ActionButton
                        onClick={handleSubmit}
                        disabled={collectionsLoading || loading || (isLocal && !isFormValid) || (!isLocal && !formData.cronExpression)}
                    >
                        {t('common.save')}
                    </ActionButton>

                    {loading && (
                        <CircularProgress
                            size={24}
                            sx={{
                                color: 'primary.main',
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                marginTop: '-12px',
                                marginLeft: '-12px',
                            }}
                        />
                    )}
                </Box>
            </DialogActions>

            {openDialog?.type === 'create' && isLocal && (
                <CreateDataSourceDialog
                    open
                    onClose={() => setOpenDialog(null)}
                    onDataSourceCreated={handleDataSourceCreated}
                />
            )}

            {openDialog?.type === 'cron' && (
                <CronConfigDialog
                    open
                    currentCron={formData.cronExpression}
                    onClose={() => setOpenDialog(null)}
                    onSave={handleSaveCron}
                    mode="create"
                />
            )}
        </Dialog>
    );
};

export default EditUpdateSchedulerDialog;